import { Country, EntityLabel } from '@esg/esg-global-types';
import { validateMasterListParams } from '../../util/validation';
import { BatchWrite } from '../app/db_util';
import { generateUuidV4 } from '../../util/uuid';
import { MetadataError } from '@ep/error-handling';
import { auth } from '../google/firebase';
import { apollo_client } from '../app/db_util';
import { GET_COUNTRIES_QUERY } from '../graphql/queries/country';
import { ADD_COUNTRY, DELETE_COUNTRY, UPDATE_COUNTRY } from '../graphql/mutations/country';
import { FUNCTION_UNKNOWN_FAILURE } from '../../util/global_variables';

export type CountryData = Omit<Country, 'id'>;

const SOURCE = 'lib/metric_capture/country.ts';

// Singular and plural label for model entity.
export const country_label: EntityLabel = {
  one: 'Country',
  many: 'Countries'
};

/**
 * Create a collection path string for the master list countries or configured countries.
 * @param {boolean} master_list Reference the master list collection.
 * @param {string} group_id When not referencing a master list collection, a group id for the configured group is required.
 * @param {string} company_id When not referencing a master list collection, a company id for the configured company is required.
 * @returns {string} Path to a countries collection.
 */
export const createCountryCollectionPath = (
  master_list: boolean,
  group_id?: string,
  company_id?: string
): string => {
  try {
    validateMasterListParams(master_list, group_id, company_id);
    return master_list
      ? `/country_master_list`
      : `groups/${group_id}/companies/${company_id}/countries`;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'createCountryCollectionPath'),
      {
        master_list: master_list,
        group_id: group_id,
        company_id: company_id
      },
      tracking_id
    );
  }
};

/**
 * Query all country documents for current portal company joined with relative country data
 * @param {boolean} master_list Return master list or configured country documents.
 * @param {string} group_id Group object of current portal company
 * @param {string} company_id id of current portal company to reference firestore documents
 * @param {string} id query a specific country by id.
 * @returns {Promise<Array<Country>>}
 */
export const getCountries = async (
  master_list: boolean,
  group_id?: string,
  company_id?: string,
  id?: string
): Promise<Array<Country>> => {
  try {
    const countries: Array<Country> = await apollo_client
      .query({
        variables: {
          master_list: !!master_list,
          group_id: group_id,
          company_id: company_id,
          id: id
        },
        query: GET_COUNTRIES_QUERY,
        fetchPolicy: 'no-cache'
      })
      .then((response) => {
        return response.data.countries;
      });
    return countries;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'getCountries'),
      {
        master_list: master_list,
        group_id: group_id,
        company_id: company_id
      },
      tracking_id
    );
  }
};

/**
 * Get the relevant Country object by international phone number from a list of countries.
 * @param {string} phone_number Phone number provided in an internation format starting with + followed by the country dialing code.
 * @param {Array<Country>} country_list A list of Country objects to validate the phone number against.
 * @returns {Country | null} Null is returned when no Country object was found within the provided list.
 */
export const getCountryFromListByPhone = (
  phone_number: string,
  country_list: Array<Country>
): Country | null => {
  const filtered_countries: Array<Country> =
    country_list && country_list.length > 1
      ? country_list.filter((country: Country) => {
          return country && country.phone_code
            ? phone_number.startsWith('+' + country.phone_code)
            : false;
        })
      : [];
  return filtered_countries.length === 1 ? filtered_countries[0] : null;
};

/**
 * Get a Flag CDN URI to a flag icon.
 * @param {string} country_code ISO 3166 country code.
 * @returns {string}
 */
export const generateFlagURI = (country_code: string): string =>
  `https://flagcdn.com/${country_code.toLowerCase()}.svg`;

/**
 * Create Country data in firestore on master list or company level
 * @param {boolean} master_list Flag to create new Country within master list or company level collection
 * @param {Array<Country | CountryData>} countries Country objects to create
 * @param {string | undefined} group_id Optional ID of Group to create countries for
 * @param {string | undefined} company_id Optional ID of Company to create countries for
 * @returns {Promise<Array<BatchWrite>>}
 */
export const createCountries = async (
  master_list: boolean,
  countries: Array<Country | CountryData>,
  group_id?: string,
  company_id?: string,
  return_batch?: boolean
): Promise<Array<BatchWrite>> => {
  try {
    const country_writes: Array<BatchWrite> = await apollo_client
      .mutate({
        mutation: ADD_COUNTRY,
        variables: {
          master_list: master_list,
          countries: countries,
          group_id: group_id,
          company_id: company_id,
          user_email: auth.currentUser?.email,
          return_batch: return_batch
        }
      })
      .then((response) => {
        return response.data.createCountries;
      });

    return country_writes;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'createCountries'),
      {
        master_list: master_list,
        group_id: group_id,
        company_id: company_id,
        countries: countries
      },
      tracking_id
    );
  }
};

/**
 * Update country with relative data
 * @param {boolean} master_list Flag to update country in master list
 * @param {Country} updated_country New Country data to push into document
 * @param {Country} original_country Old Country data
 * @param {string} group_id Optional ID of Group to update country for
 * @param {string} company_id Optional ID of Company to update country for
 * @returns {void}
 */
export const updateCountry = async (
  master_list: boolean,
  updated_country: Country,
  original_country: Country,
  group_id?: string,
  company_id?: string
): Promise<void> => {
  try {
    await apollo_client.mutate({
      mutation: UPDATE_COUNTRY,
      variables: {
        master_list: master_list,
        updated_country: updated_country,
        original_country: original_country,
        group_id: group_id,
        company_id: company_id,
        user_email: auth.currentUser?.email
      }
    });
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'updateCountry'),
      {
        group_id: group_id,
        company_id: company_id,
        updated_country: updated_country,
        original_country: original_country
      },
      tracking_id
    );
  }
};

/**
 * Soft delete a Country.
 * @param {string} group_id ID of Group to delete country for
 * @param {string} company_id ID of Company to delete country for
 * @param {string} country_id ID of country to delete
 * @param {string} country_group_id ID of country group to find delete country in
 * @returns {void}
 */
export const deleteCountry = async (
  master_list: boolean,
  country_id: string,
  country_name: string,
  group_id?: string,
  company_id?: string
): Promise<void> => {
  try {
    await apollo_client.mutate({
      mutation: DELETE_COUNTRY,
      variables: {
        master_list: master_list,
        country_id: country_id,
        country_name: country_name,
        group_id: group_id,
        company_id: company_id,
        user_email: auth.currentUser?.email
      }
    });
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'deleteCountry'),
      {
        master_list: master_list,
        group_id: group_id,
        company_id: company_id,
        country_id: country_id,
        country_name: country_name
      },
      tracking_id
    );
  }
};
